import axios, { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios'
import { ApiResponse, PaginatedResponse } from '@/types'

// Extend Axios config to include _retry flag for refresh token logic
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

// API Configuration
const API_BASE_URL = (import.meta as any).env.VITE_API_URL || 'https://api.waitlistmanager.com'

// CSRF Configuration
const CSRF_COOKIE_NAME = 'csrf_token'
const CSRF_HEADER_NAME = 'X-CSRF-Token'

// In-memory access token storage (never in localStorage)
let accessTokenInMemory: string | null = null

/**
 * Get CSRF token from cookie
 */
function getCsrfToken(): string | null {
  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=')
    if (name === CSRF_COOKIE_NAME) {
      return decodeURIComponent(value)
    }
  }
  return null
}

// Create axios instance
export const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Required for HttpOnly cookies and CSRF
})

// Request interceptor to add auth token and CSRF token
api.interceptors.request.use(
  (config) => {
    // Add JWT auth token from in-memory storage
    if (accessTokenInMemory) {
      config.headers.Authorization = `Bearer ${accessTokenInMemory}`
    }

    // Add CSRF token for state-changing requests
    const method = config.method?.toLowerCase()
    if (method && ['post', 'put', 'delete', 'patch'].includes(method)) {
      const csrfToken = getCsrfToken()
      if (csrfToken) {
        config.headers[CSRF_HEADER_NAME] = csrfToken
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig | undefined

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true

      if (!accessTokenInMemory) {
        // No access token in memory, user is not logged in
        // Only redirect if we're not already on the login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login'
        }
        return Promise.reject(error)
      }

      try {
        // Refresh token is sent automatically via HttpOnly cookie
        const response = await axios.post(
          `${API_BASE_URL}/api/v1/auth/refresh`,
          {},
          { withCredentials: true }
        )

        const { access_token } = response.data
        accessTokenInMemory = access_token

        // Retry the original request
        originalRequest.headers.Authorization = `Bearer ${access_token}`
        return api(originalRequest)
      } catch (_refreshError) {
        // Refresh failed, clear memory and redirect to login
        accessTokenInMemory = null

        // Only redirect if we're not already on the login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login'
        }
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  }
)

// API response wrapper type
type ApiMethod = <T = any>(
  url: string,
  data?: any,
  config?: any
) => Promise<ApiResponse<T>>

type PaginatedApiMethod = <T = any>(
  url: string,
  params?: any
) => Promise<PaginatedResponse<T>>

// Generic API methods
export const apiGet: ApiMethod = async (url, config) => {
  try {
    const response: AxiosResponse = await api.get(url, config)
    return {
      success: true,
      data: response.data,
    }
  } catch (error) {
    return handleApiError(error as AxiosError)
  }
}

export const apiPost: ApiMethod = async (url, data, config) => {
  try {
    const response: AxiosResponse = await api.post(url, data, config)
    return {
      success: true,
      data: response.data,
    }
  } catch (error) {
    return handleApiError(error as AxiosError)
  }
}

export const apiPut: ApiMethod = async (url, data, config) => {
  try {
    const response: AxiosResponse = await api.put(url, data, config)
    return {
      success: true,
      data: response.data,
    }
  } catch (error) {
    return handleApiError(error as AxiosError)
  }
}

export const apiPatch: ApiMethod = async (url, data, config) => {
  try {
    const response: AxiosResponse = await api.patch(url, data, config)
    return {
      success: true,
      data: response.data,
    }
  } catch (error) {
    return handleApiError(error as AxiosError)
  }
}

export const apiDelete: ApiMethod = async (url, config) => {
  try {
    const response: AxiosResponse = await api.delete(url, config)
    return {
      success: true,
      data: response.data,
    }
  } catch (error) {
    return handleApiError(error as AxiosError)
  }
}

export const apiGetPaginated: PaginatedApiMethod = async (url, params) => {
  try {
    const response: AxiosResponse = await api.get(url, { params })

    // Extract the data array - backend uses different field names (waitlists, signups, users, etc.)
    // Try common field names in order of priority
    let dataArray: any[] = []
    if (Array.isArray(response.data.data)) {
      dataArray = response.data.data
    } else if (Array.isArray(response.data.waitlists)) {
      dataArray = response.data.waitlists
    } else if (Array.isArray(response.data.signups)) {
      dataArray = response.data.signups
    } else if (Array.isArray(response.data.users)) {
      dataArray = response.data.users
    } else {
      // Fallback: find the first array field in the response (excluding pagination)
      for (const key of Object.keys(response.data)) {
        if (key !== 'pagination' && Array.isArray(response.data[key])) {
          dataArray = response.data[key]
          break
        }
      }
    }

    return {
      success: true,
      data: dataArray,
      pagination: response.data.pagination,
    }
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError)
    return {
      success: false,
      data: [],
      pagination: {
        page: 1,
        per_page: 10,
        total: 0,
        total_pages: 0,
      },
      error: errorResponse.error,
    }
  }
}

// Custom error class to preserve response details for rate limiting
export class ApiError extends Error {
  public status?: number
  public response?: {
    status: number
    data: any
    headers: Record<string, string>
  }

  constructor(message: string, status?: number, response?: any) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.response = response
  }
}

// Error handler
function handleApiError(error: AxiosError): ApiResponse<never> {
  // Only log errors that aren't auth-related, expected 404s, or rate limits
  if (error.response?.status !== 401 &&
      error.response?.status !== 404 &&
      error.response?.status !== 429) {
    console.error('API Error:', error)
  }

  if (error.response?.data) {
    const data = error.response.data as any
    // Handle FastAPI validation errors (detail is an array of objects)
    let errorMessage: string
    let errorCode: string | undefined
    if (Array.isArray(data.detail)) {
      // Extract first validation error message
      errorMessage = data.detail[0]?.msg || data.detail[0]?.message || 'Validation error'
    } else if (typeof data.error === 'object' && data.error !== null) {
      // Structured error response: { error: { code, message } }
      errorMessage = data.error.message || 'An error occurred'
      errorCode = data.error.code
    } else {
      errorMessage = data.detail || data.error || data.message || 'An error occurred'
    }

    // For rate limit errors (429), throw an ApiError with full response details
    if (error.response.status === 429) {
      const apiError = new ApiError(errorMessage, error.response.status, {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers as Record<string, string>,
      })
      throw apiError
    }

    // For reCAPTCHA errors (400 with recaptcha in message), throw ApiError
    if (error.response.status === 400 && errorMessage.toLowerCase().includes('recaptcha')) {
      const apiError = new ApiError(errorMessage, error.response.status, {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers as Record<string, string>,
      })
      throw apiError
    }

    // For email not verified errors (403 with EMAIL_NOT_VERIFIED code), throw ApiError
    if (error.response.status === 403 && errorCode === 'EMAIL_NOT_VERIFIED') {
      const apiError = new ApiError(errorMessage, error.response.status, {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers as Record<string, string>,
      })
      apiError.name = 'EMAIL_NOT_VERIFIED'
      throw apiError
    }

    return {
      success: false,
      error: errorMessage,
    }
  }

  if (error.request) {
    return {
      success: false,
      error: 'Network error. Please check your connection.',
    }
  }

  return {
    success: false,
    error: error.message || 'An unexpected error occurred',
  }
}

/**
 * Attempt to silently refresh the access token using the HttpOnly refresh cookie.
 *
 * Used on app bootstrap so a hard reload / direct deep-link doesn't log the user
 * out when only the in-memory access token is missing.
 *
 * Returns the new access token on success, or null if no valid refresh cookie
 * exists (i.e. user is genuinely logged out). Does NOT redirect on failure;
 * callers decide what to do.
 */
export async function tryRefreshAccessToken(): Promise<string | null> {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/v1/auth/refresh`,
      {},
      { withCredentials: true }
    )
    const accessToken = response.data?.access_token
    if (typeof accessToken === 'string' && accessToken.length > 0) {
      accessTokenInMemory = accessToken
      return accessToken
    }
    return null
  } catch {
    return null
  }
}

// Auth token management (access_token in memory, refresh_token via HttpOnly cookie)
export const authTokens = {
  get: () => ({
    access_token: accessTokenInMemory,
  }),

  set: (tokens: { access_token: string; refresh_token?: string }) => {
    accessTokenInMemory = tokens.access_token
    // refresh_token is handled by HttpOnly cookie set by the backend
  },

  clear: () => {
    accessTokenInMemory = null
    // refresh_token cookie is cleared by the backend on logout
  },

  isAuthenticated: () => {
    return !!accessTokenInMemory
  },
}

// Upload file helper
export const uploadFile = async (file: File, onProgress?: (progress: number) => void) => {
  const formData = new FormData()
  formData.append('file', file)

  try {
    const response = await api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      },
    })

    return {
      success: true,
      data: response.data,
    }
  } catch (error) {
    return handleApiError(error as AxiosError)
  }
}

export default api
